import {
  AHNonAnimtedPageIndicator,
  AHText,
  Box,
  ColorType,
  useRadiusSize,
  useTheme,
} from "arkh";
import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { BaseWidgetProps } from "../BaseProps";
import { StyleProp, TouchableOpacity, View, ViewStyle } from "react-native";
import { WebPageWrapper } from "./WebPageWrapper";

import { WidgetNotePage } from "./WidgetNotePage";

import { useInterval } from "../../useInterval";
import { useWidgetLayout } from "../WidgetLayoutContext";

import { graphql, useFragment } from "react-relay";

import { useNoteSubscriptions } from "./useNoteSubscriptions";
import { NoteWeb_note$key } from "./__generated__/NoteWeb_note.graphql";
import { WidgetNoteTitle } from "./WidgetNoteTitle";
import { pageForegroundColor } from "./styleHelpers/pageForegroundColor";
import { TouchPageButtons } from "./TouchPageButtons";

type WidgetNoteProps = {
  note: NoteWeb_note$key;
  isRenderedFromOrg?: boolean;
  pageId?: string;
  isTouch?: boolean;
  onPagePress: (pageId: string) => void;
} & Pick<BaseWidgetProps, "interactive">;

export function NoteWeb(props: WidgetNoteProps) {
  const note = useFragment(
    graphql`
      fragment NoteWeb_note on Note {
        id
        title
        isOrgNote
        noteSize
        editingMode
        pages {
          id
          image {
            id
          }
          fontColor
          includedInSlideshow
          backgroundColor
          ...WidgetNotePage_widgetNotePage
        }
      }
    `,
    props.note
  );
  const locked = note.isOrgNote && !props.isRenderedFromOrg;

  const [pageIdDismissed, setPageIdDismissed] = useState(false);
  const pageId = pageIdDismissed ? undefined : props.pageId;

  useNoteSubscriptions(note.id, props.interactive === false);

  const pages = useMemo(() => {
    if (pageId) {
      return note.pages;
    }
    const activePages = note.pages.filter((p) => p.includedInSlideshow);
    if (activePages.length > 0) {
      return activePages;
    }
    return note.pages;
  }, [pageId, note.pages]);

  const [_activePage, setActivePage] = useState(0);
  const onNext = useCallback(() => {
    if (!pageId) {
      setActivePage((prevPage) => prevPage + 1);
    }
  }, [pageId]);

  const { isPaused, pause } = usePause();

  useInterval(onNext, props.pageId || isPaused ? null : 12000);

  const pageLength = useMemo(() => pages.length, [pages]);
  const onPaginate = useCallback(
    (page: -1 | 1) => {
      pause(20000);
      setActivePage((prevPage) => {
        const next = (prevPage + page) % pageLength;
        if (next < 0) {
          return pageLength - 1;
        }
        return next;
      });
      setPageIdDismissed(true);
    },
    [pageLength, pause]
  );

  const activePage = useMemo(
    () => _activePage % pages.length,
    [_activePage, pages.length]
  );

  const titleColorStyle: {
    enableShadow: boolean;
    color: ColorType;
  } = useMemo(() => {
    const page = pageId
      ? pages.find((page) => page.id === pageId)
      : pages[activePage];

    if (!page) {
      return { enableShadow: false, color: "darkerText" };
    }
    const showLightColor = pageForegroundColor(page) === "LIGHT";
    return {
      enableShadow: showLightColor,
      color: showLightColor ? "lightText" : "darkerText",
    };
  }, [activePage, pageId, pages]);

  const activeId = useMemo(() => {
    if (pageId) {
      return pageId;
    }
    return pages[activePage]?.id;
  }, [activePage, pageId, pages]);
  return (
    <Wrapper
      pageId={activeId}
      onPagePress={props.onPagePress}
      isTouch={props.isTouch}
    >
      {pages.map((page, i) => (
        <WebPageWrapper
          key={page.id}
          active={pageId ? pageId === page.id : i === activePage}
        >
          <WidgetNotePage page={page} />
        </WebPageWrapper>
      ))}

      <View
        pointerEvents="none"
        style={{ position: "absolute", left: 0, top: 0 }}
      >
        <WidgetNoteTitle
          locked={locked}
          title={note.title}
          enableTextShadow={titleColorStyle.enableShadow}
          textColor={titleColorStyle.color}
        />
      </View>
      {pages.length > 1 ? (
        <>
          <Box
            pointerEvents="none"
            style={{
              position: "absolute",
              right: 0,
              top: 0,
              height: "100%",
              justifyContent: "center",
            }}
            spaceHorizontal="small"
            spaceTop="xsmall"
          >
            <AHNonAnimtedPageIndicator
              direction="vertical"
              page={
                pageId ? pages.findIndex((p) => p.id === pageId) : activePage
              }
              numberOfItems={pages.length}
            />
          </Box>
          {props.isTouch ? (
            <>
              <Box
                style={{
                  position: "absolute",

                  bottom: 0,
                  right: 0,
                }}
                spaceHorizontal="small"
                spaceBottom="small"
              >
                <TouchPageButtons onPaginate={onPaginate} />
              </Box>
            </>
          ) : null}
        </>
      ) : null}
    </Wrapper>
  );
}

function Wrapper(
  props: PropsWithChildren<{
    pageId?: string;
    onPagePress: (pageId: string) => void;
    isTouch?: boolean;
  }>
) {
  const { height, width } = useWidgetLayout();
  const theme = useTheme();
  const conrnerRadius = useRadiusSize("medium");
  const style: StyleProp<ViewStyle> = useMemo(() => {
    return {
      height,
      width,
      backgroundColor: theme.surface,
      borderRadius: conrnerRadius,
      overflow: "hidden",
    };
  }, [height, width, theme.surface, conrnerRadius]);

  if (props.isTouch) {
    return (
      <TouchableOpacity
        style={style}
        onPress={() => {
          if (props.pageId) {
            props.onPagePress(props.pageId);
          }
        }}
      >
        {props.children}
      </TouchableOpacity>
    );
  }
  return <View style={style}>{props.children}</View>;
}

function usePause() {
  const [isPaused, setIsPaused] = useState(false);
  const ref = useRef<NodeJS.Timeout | null>(null);
  const pause = useCallback((time: number) => {
    if (ref.current) {
      clearTimeout(ref.current);
      ref.current = null;
    }
    ref.current = setTimeout(() => {
      setIsPaused(true);
    }, time);
  }, []);
  return { isPaused, pause };
}
